<template>
	<div class="modal fade" id="eventDocsUploadModal" tabindex="-1" role="dialog" aria-labelledby="eventDocsUploadModalLabel" aria-hidden="true" data-backdrop="static">
		<div class="modal-dialog modal-xl" role="document">
			<div class="modal-content">
				<div class="modal-header">
					<h5 class="modal-title" id="eventDocsUploadModalLabel">{{$t('documentsUpload')}}</h5>
					<button type="button" class="close" data-dismiss="modal" aria-label="Close">
						<span aria-hidden="true">&times;</span>
					</button>
				</div>
				<div class="modal-body">
					<div class="loader" v-show="isSaving || isLoadingLecturers"></div>
					<div v-show="!isSaving && !isLoadingLecturers">
						<!-- Секция за качване на файлове -->
						<div class="mb-4">
							<h6 class="font-weight-bold mb-3">{{ $t('selectFiles') }}</h6>
							<div class="col-md-12 mb-2 d-flex align-items-center p-0" v-for="(file, index) in files" :key="'file' + index">
								<div style="flex: 1;">
									<file-crud :showDescription="false" :file="file" @change="onFileChange" :removable="false"></file-crud>
								</div>
								<div style="width: 30px">
									<span class="text-danger fa fa-trash ml-2 pointer" style="margin: 0 auto;" @click="removeFile(index)"></span>
								</div>
							</div>
							<button class="btn btn-secondary" @click="addFile" v-if="showAdd">{{$t('add')}}</button>
						</div>

						<!-- Секция за тип документ -->
						<div class="mb-4">
							<h6 class="font-weight-bold mb-3">{{ $t('documentsSettings.documentType') }}</h6>
							<c-select
								v-model="selectedDocumentType"
								:options="documentTypes"
								:clearable="false"
								:searchable="false"
								:open-on-click="true"
								:reduce="option => option.value"
								:label="'text'"
								class="doc-type-select"
							></c-select>
						</div>

						<!-- Секция за избор на преподавател -->
						<div class="mb-3" v-if="lecturers && lecturers.length > 0">
							<h6 class="font-weight-bold mb-3">{{ $t('registers.events.lecturers.title') }}</h6>
							<div class="table-responsive">
								<table class="table table-sm table-hover">
									<thead>
										<tr>
											<th style="width: 50px;"></th>
											<th>{{ $t('registers.events.participants.gridHeaders.name') }}</th>
											<th>{{ $t('registers.events.participants.gridHeaders.email') }}</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="lecturer in lecturers"
											:key="lecturer.id"
											@click="selectLecturer(lecturer)"
											:class="{ 'table-primary': selectedLecturerId === lecturer.id }"
											style="cursor: pointer;">
											<td>
												<vs-radio
													v-model="selectedLecturerId"
													:vs-value="lecturer.id"
													:name="'lecturer_' + _uid"
													style="justify-content: flex-start !important">
												</vs-radio>
											</td>
											<td>{{ isEnVersion ? lecturer.fullNameEn : lecturer.fullName }}</td>
											<td>{{ lecturer.email }}</td>
										</tr>
									</tbody>
								</table>
							</div>
							<div class="mt-2">
								<button type="button" class="btn btn-sm btn-outline-secondary" @click="clearLecturerSelection" v-if="selectedLecturerId">
									{{ $t('buttons.clearSelection') }}
								</button>
							</div>
						</div>
						<div v-else-if="!isLoadingLecturers" class="alert alert-info">
							{{ $t('registers.events.lecturers.noLecturers') }}
						</div>
					</div>
				</div>
				<div class="modal-footer">
					<button type="button" class="btn btn-primary" @click="onUpload" v-if="hasFiles && !isSaving">
						<i class="fas fa-upload mr-2"></i>{{$t('buttons.save.btnText')}}
					</button>
					<button type="button" class="btn btn-secondary" data-dismiss="modal">{{$t("buttons.cancel")}}</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { File } from '../../models/file.models';
import FileCrud from '../common/FileCRUD';
import { mapGetters } from 'vuex';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import {
	OrganizationalDocUploadTypeCode,
	getDocumentUploadTypeOptions
} from '@/common/documentUploadTypes';

export default {
	name: 'EventDocsUploadModal',
	components: {
		FileCrud,
		'c-select': vSelect
	},
	props: {
		show: {
			type: Boolean,
			default: false
		},
		eventId: {
			type: [String, Number],
			required: true
		}
	},
	data() {
		return {
			isSaving: false,
			isLoadingLecturers: false,
			files: [],
			lecturers: [],
			selectedLecturerId: null,
			selectedDocumentType: OrganizationalDocUploadTypeCode.BusinessTripOrder
		};
	},
	methods: {
		addFile() {
			this.files.push(new File({}));
		},
		removeFile(index) {
			this.files.splice(index, 1);
			if (this.files.length === 0) {
				this.files.push(new File({}));
			}
		},
		onFileChange() {
			// Опционално уведомяване на родителя
		},
		selectLecturer(lecturer) {
			this.selectedLecturerId = lecturer.id;
		},
		clearLecturerSelection() {
			this.selectedLecturerId = null;
		},
		loadLecturers() {
			this.isLoadingLecturers = true;

			// Използваме съществуващото API за преподаватели
			const request = {
				filter: {
					logic: 'and',
					filters: [
						{ field: 'registerEntityId', operator: 'eq', value: this.eventId }
					]
				}
			};

			this.$http
				.post('/api/lectors/lecturersForEvent', request)
				.then(response => {
					// API връща KendoGridResponse с items
					const items = response && response.data && response.data.items ? response.data.items : [];
					this.lecturers = items;
				})
				.catch(err => {
					console.error('Грешка при зареждане на преподаватели:', err);
					this.lecturers = [];
				})
				.finally(() => {
					this.isLoadingLecturers = false;
				});
		},
		onUpload(e) {
			e.preventDefault();

			if (this.hasFiles && !this.isSaving) {
				this.upload();
			}
		},
		upload() {
			this.isSaving = true;
			let data = new FormData();

			this.files.forEach(file => {
				if (file.content) {
					data.append('files', file.content);
				}
			});

			// Добавяне на модела като JSON
			const uploadModel = {
				registerEntityToPersonIds: this.selectedLecturerId ? [this.selectedLecturerId] : [],
				documentType: this.selectedDocumentType
			};
			data.append('uploadModelJson', JSON.stringify(uploadModel));

			const uploadUrl = `/api/events/docs/upload/${this.eventId}`;

			this.$http
				.post(uploadUrl, data)
				.then(result => {
					this.$vs.notify({
						color: 'success',
						title: this.$t('updateSuccess'),
						text: ''
					});
					this.$emit('uploaded', this.files);
					this.hide();
				})
				.catch(err => {
					this.$vs.notify({
						color: 'danger',
						title: this.$t('notifications.error'),
						text: this.$helpers.errorText(err)
					});
				})
				.finally(() => {
					this.isSaving = false;
				});
		},
		onClose() {
			this.$emit('update:show', false);
			this.$emit('onClose');
			this.resetForm();
		},
		resetForm() {
			this.files = [];
			this.lecturers = [];
			this.selectedLecturerId = null;
			this.selectedDocumentType = OrganizationalDocUploadTypeCode.BusinessTripOrder;
		},
		hide() {
			$(this.$el).modal('hide');
		}
	},
	mounted() {
		$(this.$el).on('hidden.bs.modal', this.onClose);
	},
	beforeDestroy() {
		$(this.$el).off('hidden.bs.modal');
	},
	computed: {
		...mapGetters(['isEnVersion']),
		showAdd() {
			return !this.files.some(f => f.fileName.length === 0);
		},
		hasFiles() {
			return this.files.some(x => x.fileName.length > 0);
		},
		documentTypes() {
			return getDocumentUploadTypeOptions(this.$i18n);
		}
	},
	watch: {
		show: function(val) {
			if (val) {
				this.resetForm();
				this.files.push(new File({}));
				this.loadLecturers();
				$(this.$el).modal('show');
			} else {
				$(this.$el).modal('hide');
			}
		}
	}
};
</script>

<style scoped>
.doc-type-select ::v-deep .vs__dropdown-toggle {
	min-height: 38px;
	height: 38px;
	padding: 0.375rem 0.75rem;
	border: 1px solid #ced4da;
	border-radius: 0.25rem;
	background-color: #fff;
	box-shadow: none;
	width: 100%;
	cursor: pointer;
}

.doc-type-select ::v-deep .vs__selected-options {
	padding: 0;
	flex: 1 1 auto;
}

.doc-type-select ::v-deep .vs__search,
.doc-type-select ::v-deep .vs__selected {
	margin: 0;
	cursor: pointer;
}

.doc-type-select ::v-deep .vs__actions {
	padding: 0;
}

.doc-type-select ::v-deep .vs__open-indicator {
	margin-top: 2px;
}

.table-primary {
	background-color: rgba(0, 123, 255, 0.15) !important;
}

.table tbody tr:hover {
	background-color: rgba(0, 123, 255, 0.075);
}
</style>
